import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { employeesApi, rolesApi, storesApi } from '@/api';
import { ROLE_DESCRIPTIONS, ROLES_HELP } from '@/constants/roles';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { EmployeeRequest, EmployeeResponse } from '@/types/employee';
import type { RoleResponse } from '@/types/role';
import type { StoreResponse } from '@/types/store';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PlanLimitNotice } from '@/components/ui/PlanLimitNotice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: EmployeeRequest = { employeeCode: '', firstName: '', active: true };

export function EmployeesPage() {
  const { atEmployeeLimit, employeeLimitMessage, reload: reloadLimits } = usePlanLimits();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<EmployeeResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeResponse | null>(null);
  const [form, setForm] = useState<EmployeeRequest>(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    rolesApi.listAssignable().then(setRoles).catch(() => {});
    storesApi.list({ size: 100, activeOnly: true }).then((p) => setStores(p.content)).catch(() => {});
  }, []);

  const roleOptions = useMemo(
    () =>
      roles.map((r) => ({
        value: String(r.id),
        label: r.systemRole ? `${r.name} (built-in)` : r.name,
      })),
    [roles],
  );

  const selectedRole = roles.find((r) => r.id === form.roleId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleStore = (storeId: number) => {
    const ids = form.storeIds ?? [];
    setForm({
      ...form,
      storeIds: ids.includes(storeId) ? ids.filter((id) => id !== storeId) : [...ids, storeId],
    });
  };

  const columns: Column<EmployeeResponse>[] = [
    { key: 'employeeCode', header: 'Code' },
    {
      key: 'firstName',
      header: 'Name',
      render: (r) => `${r.firstName} ${r.lastName ?? ''}`.trim(),
    },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'App role',
      render: (r) => r.roleName ?? '—',
    },
    {
      key: 'login',
      header: 'Login',
      render: (r) => (r.hasLogin ? 'Yes' : 'No'),
    },
    { key: 'designation', header: 'Designation' },
    { key: 'status', header: 'Status', render: (r) => (r.status ? 'Active' : 'Inactive') },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({
              employeeCode: r.employeeCode,
              firstName: r.firstName,
              lastName: r.lastName,
              email: r.email,
              phone: r.phone,
              roleId: r.roleId,
              designation: r.designation,
              active: r.active,
              storeIds: r.storeIds ?? [],
            });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    if (form.email?.trim() && !form.roleId) {
      notify.error('Select an app access role when email is provided');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.loginPassword) {
        delete payload.loginPassword;
      }
      if (editing) await employeesApi.update(editing.id, payload);
      else await employeesApi.create(payload);
      notify.success(editing ? 'Employee updated' : 'Employee created');
      setModalOpen(false);
      load();
      reloadLimits();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Employees"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Employees' }]}
        actions={
          <Button
            disabled={atEmployeeLimit}
            title={atEmployeeLimit ? employeeLimitMessage ?? '' : undefined}
            onClick={() => {
              setEditing(null);
              setForm(empty);
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> Add employee
          </Button>
        }
      />
      {atEmployeeLimit && employeeLimitMessage && <PlanLimitNotice message={employeeLimitMessage} />}

      <div className="card mb-4 p-4 text-sm text-slate-600 dark:text-slate-400">
        <p className="font-medium text-slate-800 dark:text-slate-200">{ROLES_HELP.title}</p>
        <p className="mt-1">{ROLES_HELP.body}</p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">{ROLES_HELP.owner}</p>
      </div>

      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit employee' : 'New employee'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Employee code *"
            value={form.employeeCode}
            onChange={(e) => setForm({ ...form, employeeCode: e.target.value })}
          />
          <Input
            label="First name *"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Input
            label="Last name"
            value={form.lastName ?? ''}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
          <Input
            label="Designation"
            value={form.designation ?? ''}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
          />
          <Input
            label="Email (for app login)"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Input
            label="Phone"
            value={form.phone ?? ''}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Select
            label="App access role"
            placeholder="Select role (required if email set)"
            options={roleOptions}
            value={form.roleId != null ? String(form.roleId) : ''}
            onChange={(v) =>
              setForm({
                ...form,
                roleId: v ? Number(v) : undefined,
              })
            }
          />
          <Input
            label={editing?.hasLogin ? 'New login password (optional)' : 'Initial login password'}
            type="password"
            placeholder={editing?.hasLogin ? 'Leave blank to keep current' : 'Default: ChangeMe@123'}
            value={form.loginPassword ?? ''}
            onChange={(e) => setForm({ ...form, loginPassword: e.target.value })}
          />
        </div>

        {selectedRole && (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-300">{selectedRole.name}: </span>
            {ROLE_DESCRIPTIONS[selectedRole.code] ?? selectedRole.description ?? 'Custom role permissions apply.'}
          </p>
        )}

        {stores.length > 0 && (
          <div className="mt-4">
            <p className="label">Assigned stores</p>
            <div className="flex flex-wrap gap-2">
              {stores.map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-slate-600"
                >
                  <input
                    type="checkbox"
                    checked={(form.storeIds ?? []).includes(s.id)}
                    onChange={() => toggleStore(s.id)}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          </div>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            type="checkbox"
            checked={form.active !== false}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          Active employee (login disabled when inactive)
        </label>
      </Modal>
    </div>
  );
}
